import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Chip, IconButton, Stack, TextField, Typography, Box, Button } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";

import ArtworkPicker from "../Portfolios/ArtworkPicker";
import { LinkedEntityType } from "../../models/diaryTypes";
import type { DiaryLinkDto } from "../../models/diaryTypes";
import type { ArtworkStub } from "../../models/Artwork";

import {
    getArtworkIdsFromLinks,
    getSelectedArtworkStubs,
    buildArtworkLabel,
    mergeArtworkLinksPreservingOtherLinks,
} from "./link-to-helper";

type Props = {
    links: DiaryLinkDto[];
    setLinks: React.Dispatch<React.SetStateAction<DiaryLinkDto[]>>;
};
export default function LinksEditor({ links, setLinks }: Props) {

    const navigate = useNavigate();

    // IDs derived from persisted links
    const selectedArtworkIds = useMemo(
        () => getArtworkIdsFromLinks(links),
        [links]
    );

    const [artworks, setArtworks] = useState<ArtworkStub[]>([]);

    // Stubs derived from the known artwork list
    const selectedArtworkStubs = useMemo(
        () => getSelectedArtworkStubs(artworks, selectedArtworkIds),
        [artworks, selectedArtworkIds]
    );

    function handleArtworkIdsChange(newIds: number[]) {
        // Convert IDs -> stubs (only ones we can resolve)
        const stubs = getSelectedArtworkStubs(artworks, newIds);

        setLinks(prev => mergeArtworkLinksPreservingOtherLinks(prev, stubs));
    }

    const hasArtworkLinks = selectedArtworkIds.length > 0;

    return (
        <Stack spacing={1.25}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Links
            </Typography>

            {/* Artwork linking */}
            <Box>
                <ArtworkPicker selected={selectedArtworkIds} onChange={handleArtworkIdsChange} expandView={false} artworkScope={setArtworks} />
            </Box>

            {/* Preview chips (labelled) */}
            {hasArtworkLinks && (
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                    {selectedArtworkStubs.map(a => (
                        <Chip
                            key={a.artworkId}
                            size="small"
                            label={buildArtworkLabel(a)}
                            clickable
                            onClick={() => navigate(`/artwork/${a.artworkId}`)}
                            variant="outlined"
                        />
                    ))}

                    {/* If some selected ids are not resolvable into stubs (e.g., paging), show fallback */}
                    {selectedArtworkIds
                        .filter(id => !selectedArtworkStubs.some(a => a.artworkId === id))
                        .map(id => (
                            <Chip
                                key={`missing-${id}`}
                                size="small"
                                label={`Artwork #${id}`}
                                clickable
                                onClick={() => navigate(`/artwork/${id}`)}
                                variant="outlined"
                            />
                        ))}
                </Stack>
            )}

            {/* Future: additional entity link editors can sit below (Portfolio, Document, etc.) */}
        </Stack>
    );
}
